import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import { useLanguage } from "../../hooks/use-language";

// Success Popup Component
export const SuccessPopup = ({ show, message, onClose }) => {
  const { t } = useLanguage();

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-start justify-center pt-24 px-4 pointer-events-none"
          data-testid="success-popup-overlay"
        >
          <motion.div
            initial={{ y: -100, opacity: 0, scale: 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -50, opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="pointer-events-auto relative max-w-md w-full"
          >
            {/* Glass Card */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FF2E63]/20 via-white/10 to-white/5 backdrop-blur-2xl border border-[#FF2E63]/30 shadow-[0_0_60px_rgba(255,46,99,0.4)]">
              {/* Glow Effect */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-[#FF2E63]/30 blur-3xl" />

              {/* Content */}
              <div className="relative z-10 p-6 sm:p-8 flex items-start gap-4">
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring", damping: 15 }}
                  className="flex-shrink-0"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF2E63] to-pink-400 flex items-center justify-center shadow-[0_0_20px_rgba(255,46,99,0.5)]">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                </motion.div>

                {/* Text */}
                <div className="flex-1 pt-1">
                  <motion.h3
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="font-display font-bold text-xl text-white mb-2"
                  >
                    {t("success.title")}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-white/70 text-sm leading-relaxed"
                  >
                    {message}
                  </motion.p>
                </div>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="flex-shrink-0 text-white/50 hover:text-white transition-colors p-1"
                  data-testid="close-popup-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Progress Bar */}
              <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: 5, ease: "linear" }}
                className="h-1 bg-gradient-to-r from-[#FF2E63] to-pink-400 origin-left"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
